/**
 * Keyboard navigation targets derived from the rendered grid: rows come from
 * the `<tr>` a field sits in, columns from its horizontal span.
 */

export interface NavField {
  id: string;
  element: any;
  rowKey: string;
  /** Vertical navigation never leaves the table */
  tableKey: string;
  rowTop: number;
  top: number;
  left: number;
  right: number;
  centerX: number;
}

export type VerticalDirection = "Up" | "Down";

const ROW_GROUPING_THRESHOLD_PX = 18;

const elementKeys = new WeakMap<Element, string>();
let nextElementKey = 0;

const keyForElement = (el: Element | null, prefix: string): string => {
  if (!el) return `${prefix}:none`;
  let key = elementKeys.get(el);
  if (!key) {
    nextElementKey += 1;
    key = `${prefix}:${nextElementKey}`;
    elementKeys.set(el, key);
  }
  return key;
};

const closestElement = (element: any, selector: string): Element | null => {
  if (!element || typeof element.closest !== "function") return null;
  return element.closest(selector);
};

const isNavigable = (element: any): boolean => {
  if (!element || typeof element.getBoundingClientRect !== "function") {
    return false;
  }
  if (element.disabled) return false;
  // Collapsed columns and hidden cells render at zero size
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/** Measures an element that takes the key handler without registering a ref. */
export const measureField = (id: string, element: any): NavField | null => {
  if (!isNavigable(element)) return null;

  const rect = element.getBoundingClientRect();
  return {
    id,
    element,
    rowKey: keyForElement(closestElement(element, "tr"), "tr"),
    tableKey: keyForElement(closestElement(element, "table"), "table"),
    rowTop: rect.top,
    top: rect.top,
    left: rect.left,
    right: rect.right,
    centerX: rect.left + rect.width / 2,
  };
};

/** Navigable fields from the registered refs, ordered top-to-bottom, left-to-right. */
export const buildFieldLayout = (entries: [string, any][]): NavField[] => {
  const fields: NavField[] = [];

  entries.forEach(([id, element]) => {
    const field = measureField(id, element);
    if (field) fields.push(field);
  });

  fields.sort((a, b) => a.top - b.top || a.left - b.left);

  // Fields outside a table are grouped into rows by vertical proximity
  let geometryRowKey = "";
  let geometryRowTop = 0;
  fields.forEach(field => {
    if (field.rowKey !== "tr:none") return;

    if (
      geometryRowKey === "" ||
      Math.abs(field.top - geometryRowTop) > ROW_GROUPING_THRESHOLD_PX
    ) {
      nextElementKey += 1;
      geometryRowKey = `geom:${nextElementKey}`;
      geometryRowTop = field.top;
    }
    field.rowKey = geometryRowKey;
    field.rowTop = geometryRowTop;
  });

  // Cells in a row can align differently, so order rows by their topmost field
  const rowTops = new Map<string, number>();
  fields.forEach(field => {
    const current = rowTops.get(field.rowKey);
    if (current === undefined || field.top < current) {
      rowTops.set(field.rowKey, field.top);
    }
  });
  fields.forEach(field => {
    field.rowTop = rowTops.get(field.rowKey) ?? field.top;
  });

  fields.sort(
    (a, b) => a.rowTop - b.rowTop || a.left - b.left || a.top - b.top,
  );

  return fields;
};

/** Previous/next field in visual order, across rows and sections. */
export const findFlatNeighbor = (
  fields: NavField[],
  sourceIndex: number,
  delta: number,
): NavField | null => fields[sourceIndex + delta] ?? null;

const orderedRowsIn = (
  fields: NavField[],
  tableKey: string,
): { rowKey: string; rowTop: number }[] => {
  const rows = new Map<string, number>();
  fields.forEach(field => {
    if (field.tableKey !== tableKey) return;
    if (!rows.has(field.rowKey)) rows.set(field.rowKey, field.rowTop);
  });

  return Array.from(rows, ([rowKey, rowTop]) => ({ rowKey, rowTop })).sort(
    (a, b) => a.rowTop - b.rowTop,
  );
};

const pickClosestInRow = (
  candidates: NavField[],
  source: NavField,
): NavField | null => {
  let best: NavField | null = null;
  let bestOverlap = -Infinity;
  let bestEdge = Infinity;
  let bestCenter = Infinity;

  candidates.forEach(candidate => {
    const overlap =
      Math.min(candidate.right, source.right) -
      Math.max(candidate.left, source.left);
    // Cells line up on their left edge, so a wide colspan lands on the first
    // field of the group below rather than the middle one
    const edge = Math.abs(candidate.left - source.left);
    const center = Math.abs(candidate.centerX - source.centerX);

    if (
      overlap > bestOverlap ||
      (overlap === bestOverlap &&
        (edge < bestEdge || (edge === bestEdge && center < bestCenter)))
    ) {
      best = candidate;
      bestOverlap = overlap;
      bestEdge = edge;
      bestCenter = center;
    }
  });

  return best;
};

/**
 * Field above/below the source within the same table. Rows with no navigable
 * field are skipped; navigation stops at the first/last row of the table.
 */
export const findVerticalNeighbor = (
  fields: NavField[],
  source: NavField,
  direction: VerticalDirection,
): NavField | null => {
  const step = direction === "Down" ? 1 : -1;
  const rows = orderedRowsIn(fields, source.tableKey);
  if (rows.length === 0) return null;

  const sourcePosition = rows.findIndex(row => row.rowKey === source.rowKey);

  let index: number;
  if (sourcePosition !== -1) {
    index = sourcePosition + step;
  } else if (direction === "Down") {
    // The source row registered no fields, so locate it by vertical position
    index = rows.findIndex(
      row => row.rowTop > source.top + ROW_GROUPING_THRESHOLD_PX,
    );
    if (index === -1) return null;
  } else {
    index = -1;
    rows.forEach((row, position) => {
      if (row.rowTop < source.top - ROW_GROUPING_THRESHOLD_PX) index = position;
    });
    if (index === -1) return null;
  }

  while (index >= 0 && index < rows.length) {
    const candidates = fields.filter(
      field =>
        field.tableKey === source.tableKey &&
        field.rowKey === rows[index].rowKey &&
        field.element !== source.element,
    );

    const next = pickClosestInRow(candidates, source);
    if (next) return next;

    index += step;
  }

  return null;
};
